import {Color} from '@benchling/nucleus-ui';

import {AnalyticsMetadataForContext} from 'analytics/AnalyticsTypes';
import {FixedPlateModalType} from 'constants/analytics-products/SamplesAnalyticsConstants';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {Measurement} from 'constants/MeasurementConstants';
import {PrimitiveValue} from 'data-frames/PrimitiveValueEditor';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {MinimalSchemaField, MinimalSchemaTag} from 'folder-items/FileConstants';
import {StudyDesignType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ExperimentalRole} from 'samples/experimental-roles/ExperimentalRoleConstants';
import {
  PlateMapMeasurement,
  PlateRecordMeasurement,
} from 'samples/plate-maps/plate-record/PlateRecordConstants';
import {
  MaterialInputWithError,
  MeasurementWithErrors,
} from 'samples/plate-maps/transfer-table/validation/FillPlateTransferTableValidationConstants';
import {
  PlateMapErrorLevel,
  ValidationErrorsByLayerAndLevel,
} from 'samples/plate-maps/validation/ValidationTypes';
import {FieldType, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {BLUE, GRAY_SEMIDARK} from 'ui-library/Colors';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

/**
 * A plate layout where there exists a well for every position, and wells are ordered
 * by position, first by row then by column.
 * This can represent an existing plate, a plate to be created or a plate template.
 * TODO(BNCH-77687): handle cells with missing containers to represent matrix plates and boxes
 */
interface BasePlateMap {
  id: string | null;
  size: PlateMapSize;
  wells: Array<PlateMapWell>;
  /** Optional mapping of data parameter version IDs to their display names. */
  dataParameterNamesByVersionId?: {[schemaFieldId: string]: string};
  testOrderDilutionInfoById?: TestOrderInfoById;
  concentrationLayerInfoById?: MeasurementLayerById;
  metadataLayerInfoById?: {[layerId: string]: MetadataLayerInfo};
}

export interface MeasurementLayerById {
  [layerId: string]: MeasurementLayer;
}

export interface MeasurementLayer {
  layerId: string;
  name: string;
}

// TODO (BNCH-134932): Remove this alias
export interface ConcentrationLayerInfo extends MeasurementLayer {}

export interface MetadataLayerInfo {
  layerId: string;
  name: string;
  type: LeafType;
}

export interface MeasurementLayerValue<TMeasurement extends PlateRecordMeasurement | PlateMapMeasurement> {
  layerId: string;
  value: TMeasurement | null;
}

export type PlateMap = BasePlateMap;

export interface PlateMapSize {
  numRows: number;
  numColumns: number;
}

export interface PlateMapWell {
  position: WellPosition;
  role: ExperimentalRole | null;
  /** Container or bioentity sources to be transferred to the well */
  sources: Array<Source>;
  /** Bioentity constants that are contained in the well */
  contents: Array<BioentityContent>;
  /** Removed existing contents */
  removedContents?: Array<BioentityContent>;
  /** Schema tag values for the container of this well mapped by api ID of the field */
  schemaTags: {[schemaFieldId: string]: MinimalSchemaTag};
  /**
   * Schema tag values for data parameters assigned to this well mapped by the data parameter value schema
   * field api ID. Note that each well is expected to have a value for each data parameter applicable to the
   * plate map.
   */
  dataParameterTags: {[schemaFieldId: string]: MinimalSchemaTag};
  /** Total quantity present in the well (of contents only) */
  quantity: Measurement;
  /** Total quantity that will be present in the well after sources are transferred */
  finalQuantity: MeasurementWithErrors<Measurement>;
  /** Well concentration layers mapped by layer ID to the concentration value */
  concentrationLayers: {[layerId: string]: Measurement};
  /** Well metadata layers mapped by layer ID to the metadata value */
  metadataLayers?: {[layerId: string]: PrimitiveValue};
}

// TODO: Condense with GridCellSummary in SampleConstants.ts
export interface WellPosition {
  row: number;
  column: number;
}

export enum ReagentType {
  /** A transfer source that is a container. */
  SOURCE_CONTAINER = 'SOURCE_CONTAINER',
  /** A transfer source that is a single containable. */
  SOURCE_CONTAINABLE = 'SOURCE_CONTAINABLE',
  /** A well in a plate map that is being transferred into. */
  WELL = 'WELL',
  /** An existing content within a well. */
  WELL_CONTENT = 'WELL_CONTENT',
}

export enum WellOpacityMode {
  NONE = 'NONE',
  CONCENTRATION = 'CONCENTRATION',
  TRANSFER_QUANTITY = 'TRANSFER_QUANTITY',
}

export const INACCESSIBLE_GROUP_ID_SENTINEL = 'INACCESSIBLE';
export const INACCESSIBLE_ASSIGNMENT_GROUP = {
  id: INACCESSIBLE_GROUP_ID_SENTINEL,
  name: 'No access',
  color: Color.GRAY_400,
  value: {id: INACCESSIBLE_GROUP_ID_SENTINEL, iconId: 'ban' as BenchlingIconName},
};

/**
 * Information about a reagent (i.e. container or containable) being added to well(s) as a source
 * or existing contents contained in well(s).
 *
 * This data structure is reused between flows for filling plates and flows for displaying existing plate
 * contents, because the shape needed for both concepts is the same.
 *
 * Although 'reagent' does not necessarily reflect the role of every entity in a plate, it also does not imply
 * that the data structure is only intended for one of the flows and not the other, as well as avoiding
 * overloading some already overloaded terms like 'content.'
 */
export interface BaseReagent {
  /** The API identifier of the reagent entity. */
  id: string;
  /**
   * An id denoting a grouping of reagents. Uses the storable schema API id if the reagent is a container,
   * uses the tag schema API id if the reagent is a bioentity.
   */
  groupId: string;
}

/**
 * Information about a source reagent (i.e. container or containable) being added to a well to fill a plate.
 */
export interface BaseSource extends BaseReagent {
  /** A label for the source; e.g. the pattern group index while under pattern preview */
  label?: number;
  /**
   * How much of the reagent is being transferred. Should only be null on 'leaf' sources (i.e. contained
   * bioentity sources)
   */
  transferQuantity: MeasurementWithErrors<Measurement>;
}

/**
 * Information about an existing content in a plate well.
 */
export interface BioentityContent extends BaseReagent {
  type: ReagentType.WELL_CONTENT;
  /** The concentration of the content in the well. */
  initialConcentration: Measurement | null;
  /** The concentration of the content after additional reagents are added. */
  finalConcentration: MeasurementWithErrors<Measurement>;
  /** A procedure material input to associate this source with */
  materialInput?: MaterialInputWithError;
  /** Test orders associated with this source that have been selected to propagate to destination wells. */
  testOrders?: Array<TestOrder>;
}

/** Enum of possible preview source error types. Used to store errors in modal state. */
export enum PreviewSourceError {
  INVALID_TRIAL_SAMPLE = 'INVALID_TRIAL_SAMPLE',
}

export interface TestOrder {
  id: string;
}

export interface TestOrderInfo {
  id: string;
  name: string;
  dilutionFactor: number | null;
}

export interface InaccessibleTestOrderInfo {
  invalidId: string;
}

export function isAccessibleTestOrderInfo(
  info: TestOrderInfo | InaccessibleTestOrderInfo
): info is TestOrderInfo {
  return info.hasOwnProperty('dilutionFactor');
}

export const NULL_TEST_DILUTION_FACTOR_SENTINEL = 'NULL_TEST_DILUTION_FACTOR';
export const NO_ACCESS_TEST_DILUTION_FACTOR_SENTINEL = 'NO_ACCESS_TEST_DILUTION_FACTOR';
export const NULL_TEST_DILUTION_FACTOR_COLOR = Color.GRAY_400;

export type TestDilutionFactor =
  | number
  | typeof NULL_TEST_DILUTION_FACTOR_SENTINEL
  | typeof NO_ACCESS_TEST_DILUTION_FACTOR_SENTINEL;

export interface MethodDefinitionVersionTestOrderConfig {
  testDefinitionIds: Array<string>;
  allowedDilutionFactors: Array<number>;
}

export interface TestOrderInfoById {
  [testOrderId: string]: TestOrderInfo;
}

export type SourceErrorsBySourceDilutionId = {
  [errorType in PreviewSourceError]: {
    [sourceDilutionId: string]: string;
  };
};

export const DEFAULT_SOURCE_ERRORS_BY_SOURCE_DILUTION_ID: SourceErrorsBySourceDilutionId = {
  [PreviewSourceError.INVALID_TRIAL_SAMPLE]: {},
};

export interface PlateMapProcedureContext {
  /* Whether plate map is in a procedure context. Could probably be consolidated with procedureId. */
  isInProcedure: boolean;
  /* Procedure id, if in a procedure context. */
  procedureId: string | null;
  /* Design type of the associated procedure's study. This controls showing test order/dilution related
  information only for sample testing studies. */
  procedureStudyDesignType: StudyDesignType | null;
  /* Data parameters configured to be confirmed via plate maps. */
  plateDataParameters?: Array<PlateDataParameter>;
  /* Material inputs configured to be confirmed via plate maps. */
  plateMaterialInputs?: Array<PlateMaterialInput>;
  /* Test order configuration from the procedure. */
  procedureTestOrderConfig?: MethodDefinitionVersionTestOrderConfig | null;
  /* Procedure method execution instance ID, if in a procedure execution context. */
  procedureMethodExecutionInstanceId: string | null;
}

export interface BioentitySource extends BaseSource {
  type: ReagentType.SOURCE_CONTAINABLE;
  /**
   * The concentration of the reagent being transferred.
   */
  sourceConcentration: MeasurementWithErrors<Measurement>;
  /** The final concentration of the reagent after transfer. Can be null on any bioentity reagent. */
  finalConcentration: MeasurementWithErrors<Measurement>;
  /** A procedure material input to associate this source with */
  materialInput?: MaterialInputWithError;
  /** Test orders associated with this source that have been selected to propagate to destination wells. */
  testOrders?: Array<TestOrder>;
}

export interface ContainerSource extends BaseSource {
  type: ReagentType.SOURCE_CONTAINER;
  contents: Array<BioentitySource>;
  /** If the container is itself contained within a plate, e.g. a plate well, the API Id of that plate */
  parentPlateId?: string;
  /** The quantity (volume) of contents currently in the container */
  quantity: Measurement | null;
}

export interface ContentGroup {
  type: ReagentType.WELL;
  contents: Array<BioentityContent>;
  quantity: Measurement;
}

export type Source = BioentitySource | ContainerSource;
export type PlateMapBioentity = BioentitySource | BioentityContent;

export interface AssignmentGroupValueTypeByLayer {
  [PlateMapWellDisplayLayer.REAGENT]: {id: string; iconId: BenchlingIconName};
  [PlateMapWellDisplayLayer.SCHEMA_FIELD_SELECTOR]: string;
  [PlateMapWellDisplayLayer.WELL_ROLE]: never;
  [PlateMapWellDisplayLayer.SCHEMA_FIELD_TEXT]: {text: string; url: string};
  [PlateMapWellDisplayLayer.SCHEMA_FIELD_NUMBER]: number;
  [PlateMapWellDisplayLayer.TEST_DILUTION_FACTOR]: TestDilutionFactor;
  [PlateMapWellDisplayLayer.CONCENTRATION]: number;
  [PlateMapWellDisplayLayer.MEASUREMENT]: number;
  [PlateMapWellDisplayLayer.WELL_VOLUME]: never;
  [PlateMapWellDisplayLayer.METADATA_TEXT]: string;
  [PlateMapWellDisplayLayer.METADATA_NUMBER]: number;
  [PlateMapWellDisplayLayer.METADATA_BOOLEAN]: boolean;
}

export type SchemaFieldDisplayLayer =
  | PlateMapWellDisplayLayer.SCHEMA_FIELD_NUMBER
  | PlateMapWellDisplayLayer.SCHEMA_FIELD_SELECTOR
  | PlateMapWellDisplayLayer.SCHEMA_FIELD_TEXT;

export type AssignmentGroupValue = AssignmentGroupValueTypeByLayer[keyof AssignmentGroupValueTypeByLayer];

/**
 * Information about a group of data (i.e. reagents, concentrations) being assigned to a set of wells.
 */
export interface AssignmentGroup<T> {
  /** An id denoting a grouping of properties. */
  id: string;
  /** A CSS hex code */
  color: string;
  /** The name of the group */
  name: string;
  /** Value that is assigned to all wells with this group */
  value: T;
}

export type ReagentAssignmentGroup = AssignmentGroup<
  AssignmentGroupValueTypeByLayer[PlateMapWellDisplayLayer.REAGENT]
>;
export type SchemaFieldAssignmentGroup =
  | AssignmentGroup<AssignmentGroupValueTypeByLayer[PlateMapWellDisplayLayer.SCHEMA_FIELD_NUMBER]>
  | AssignmentGroup<AssignmentGroupValueTypeByLayer[PlateMapWellDisplayLayer.SCHEMA_FIELD_SELECTOR]>
  | AssignmentGroup<AssignmentGroupValueTypeByLayer[PlateMapWellDisplayLayer.SCHEMA_FIELD_TEXT]>;

// Defines how wells are rendered in plate map views and what well information to display.
export enum PlateMapWellDisplayLayer {
  WELL_ROLE = 'WELL_ROLE',
  REAGENT = 'REAGENT',
  CONCENTRATION = 'CONCENTRATION',
  SCHEMA_FIELD_TEXT = 'SCHEMA_FIELD_TEXT',
  SCHEMA_FIELD_NUMBER = 'SCHEMA_FIELD_NUMBER',
  SCHEMA_FIELD_SELECTOR = 'SCHEMA_FIELD_SELECTOR',
  TEST_DILUTION_FACTOR = 'TEST_DILUTION_FACTOR',
  MEASUREMENT = 'MEASUREMENT',
  WELL_VOLUME = 'WELL_VOLUME',
  METADATA_TEXT = 'METADATA_TEXT',
  METADATA_NUMBER = 'METADATA_NUMBER',
  METADATA_BOOLEAN = 'METADATA_BOOLEAN',
}

// Sentinel values to use for displaying non-schema field/parameter layers
export const WELL_ROLE_ATTRIBUTE_SENTINEL = 'WELL_ROLE_ATTRIBUTE_SENTINEL';
export const REAGENTS_ATTRIBUTE_SENTINEL = 'REAGENTS_ATTRIBUTE_SENTINEL';
export const TEST_DILUTION_FACTOR_ATTRIBUTE_SENTINEL = 'TEST_DILUTION_FACTOR_ATTRIBUTE_SENTINEL';
export const CONCENTRATION_ATTRIBUTE_SENTINEL = 'CONCENTRATION_ATTRIBUTE_SENTINEL';
export const WELL_VOLUME_ATTRIBUTE_SENTINEL = 'WELL_VOLUME_ATTRIBUTE_SENTINEL';
export const METADATA_ATTRIBUTE_SENTINEL = 'METADATA_ATTRIBUTE_SENTINEL';

// A section of display layer options in the sidebar
export enum DisplayLayerOptionSection {
  WELL_ROLE = 'WELL_ROLE',
  CONTENTS = 'CONTENTS',
  CONCENTRATION = 'CONCENTRATION',
  METADATA_FIELDS = 'METADATA_FIELDS',
  SCHEMA_FIELDS = 'SCHEMA_FIELDS',
  PARAMETERS = 'PARAMETERS',
  TEST_METADATA = 'TEST_METADATA',
}

// Color information for well display - can be solid colors or a gradient
interface WellDisplaySolidColor {
  type: 'solid';
  colors: Array<string>;
}

interface WellDisplayGradientColor {
  type: 'gradient';
  color: string;
}

export type WellColorInfo = WellDisplaySolidColor | WellDisplayGradientColor;

// Attributes describing how to render a well for a certain display layer
export interface WellDisplayInfo {
  colorInfo: WellColorInfo;
  label: string | null;
  opacity?: number;
  error?: PlateMapErrorLevel | null;
}

export enum WellAssignmentMethod {
  SPECIFY_GROUP = 'SPECIFY_GROUP',
  PATTERN = 'PATTERN',
}

// Direction options when assigning attributes to wells in a pattern
export enum PatternDirection {
  DOWN = 'DOWN',
  RIGHT = 'RIGHT',
}

export const PATTERN_DIRECTION_TO_DISPLAY: {
  [dir in PatternDirection]: {icon: BenchlingIconName; name: string};
} = {
  [PatternDirection.DOWN]: {icon: 'arrow-down', name: 'Down'},
  [PatternDirection.RIGHT]: {icon: 'arrow-right', name: 'Right'},
};

export interface PatternAssignmentParameters {
  startingGroup: number;
  fillDirection: PatternDirection;
  replicates: number;
  replicateDirection: PatternDirection;
}

export const DEFAULT_PATTERN_PARAMS = {
  startingGroup: 1,
  fillDirection: PatternDirection.DOWN,
  replicates: 1,
  replicateDirection: PatternDirection.RIGHT,
};

// Representation of a plate map with additional display attributes for rendering.
// Wells are ordered by position, first by row then by column.
export interface DisplayPlateMap {
  size: PlateMapSize;
  wells: Array<DisplayPlateMapWell>;
}

export type DisplayPlateMapWell = PlateMapWell & {
  displayInfo: WellDisplayInfo | null;
};

/* Size in pixels of plate map legend icons */
export const PLATE_MAP_LEGEND_ICON_SIZE = 10;

export interface PlateMapLegendItem {
  label: string;
  icon: JSX.Element | null;
  count: number;
  error?: boolean;
}

export type SupportedAdditionalAttribute =
  | typeof WELL_ROLE_ATTRIBUTE_SENTINEL
  | typeof REAGENTS_ATTRIBUTE_SENTINEL
  | typeof TEST_DILUTION_FACTOR_ATTRIBUTE_SENTINEL
  | typeof WELL_VOLUME_ATTRIBUTE_SENTINEL;

export enum SchematizedAttributeType {
  WELL_SCHEMA_FIELD = 'WELL_SCHEMA_FIELD',
  DATA_PARAMETER = 'DATA_PARAMETER',
}

export interface BaseSchematizedAttribute<TSchemaField extends MinimalSchemaField> {
  /** Api ID of the schema field. This matches the key under which tags are stored on PlateMapWells. */
  key: string;
  /** The schema field for the attribute. Values of the attribute are the corresponding MinimalSchemaTag type. */
  schemaField: TSchemaField;
  /** Type of the attribute, either well schema field or data parameter. */
  type: SchematizedAttributeType;
  /** Planned values for the attribute. Currently only used for data parameters. */
  plannedValues: Array<MinimalSchemaTag>;
}

export type SchematizedAttribute =
  | BaseSchematizedAttribute<MinimalSchemaField>
  | BaseSchematizedAttribute<SchemaField>;

/**
 * Represents a concentration layer attribute for plate maps.
 *
 * Similar to schematized attributes but for concentration layers. This is required to represent each
 * concentration layer as an option on the sidebar.
 */
export interface ConcentrationAttribute {
  /** Unique ID of the concentration layer */
  key: string;
  /** Type identifier for concentration attributes */
  type: typeof CONCENTRATION_ATTRIBUTE_SENTINEL;
  /** Display name of the concentration layer */
  name: string;
}

/**
 * Types of measurements that can be displayed as plate map layers
 */
export enum PlateMapMeasurementType {
  FINAL_CONCENTRATION = 'FINAL_CONCENTRATION',
  TRANSFER_QUANTITY = 'TRANSFER_QUANTITY',
  CONTENT_CONCENTRATION = 'CONTENT_CONCENTRATION',
}

/**
 * Represents a measurement attribute for plate maps
 *
 * Used to display final concentrations or transfer quantities for specific reagent groups.
 */
export interface MeasurementAttribute {
  /** Unique reagent group ID */
  key: string;
  /** Type of measurement to display */
  type: PlateMapMeasurementType;
  /** Color associated with the reagent group */
  color: string;
}

export interface MetadataAttribute {
  key: string;
  type: typeof METADATA_ATTRIBUTE_SENTINEL;
  name: string;
  dataType: LeafType;
}

export type PlateMapDisplayLayerAttribute =
  | SupportedAdditionalAttribute
  | SchematizedAttribute
  | ConcentrationAttribute
  | MeasurementAttribute
  | MetadataAttribute;

export interface DisplayLayerMetadata {
  attribute: PlateMapDisplayLayerAttribute;
  displayName: string;
  icon: BenchlingIconName;
}

export interface LiveSchematizedDisplayLayerMetadata extends DisplayLayerMetadata {
  attribute: BaseSchematizedAttribute<SchemaField>;
  outdated?: false;
}

export interface OutdatedSchematizedDisplayLayerMetadata extends DisplayLayerMetadata {
  attribute: BaseSchematizedAttribute<MinimalSchemaField>;
  outdated?: true;
}

export type SchematizedDisplayLayerMetadata =
  | LiveSchematizedDisplayLayerMetadata
  | OutdatedSchematizedDisplayLayerMetadata;

export type SupportedFieldTypeForAnnotation =
  | FieldType.TEXT
  | FieldType.SELECTOR
  | FieldType.INTEGER
  | FieldType.FLOAT;
export const SUPPORTED_FIELD_TYPES_FOR_ANNOTATION = new Set([
  FieldType.TEXT,
  FieldType.SELECTOR,
  FieldType.INTEGER,
  FieldType.FLOAT,
]);

export type SupportedFieldTypeForDataParameters = FieldType.TEXT | FieldType.FLOAT;
export const SUPPORTED_FIELD_TYPES_FOR_DATA_PARAMETERS = new Set([FieldType.TEXT, FieldType.FLOAT]);

export type SupportedFieldTypeForMaterialInputs = FieldType.BIOENTITY_LINK;
export const SUPPORTED_FIELD_TYPES_FOR_MATERIAL_INPUTS = new Set([FieldType.BIOENTITY_LINK]);

export type SupportedFieldType =
  | SupportedFieldTypeForAnnotation
  | SupportedFieldTypeForDataParameters
  | SupportedFieldTypeForMaterialInputs;

export const ADDITIONAL_ATTRIBUTE_TO_DROPDOWN_OPTION: {
  [attr in SupportedAdditionalAttribute]: DisplayLayerMetadata;
} = {
  [WELL_ROLE_ATTRIBUTE_SENTINEL]: {
    attribute: WELL_ROLE_ATTRIBUTE_SENTINEL,
    displayName: 'Well role',
    icon: 'text',
  },
  [REAGENTS_ATTRIBUTE_SENTINEL]: {
    attribute: REAGENTS_ATTRIBUTE_SENTINEL,
    displayName: 'Contents',
    icon: 'flask',
  },
  [TEST_DILUTION_FACTOR_ATTRIBUTE_SENTINEL]: {
    attribute: TEST_DILUTION_FACTOR_ATTRIBUTE_SENTINEL,
    displayName: 'Dilution factor',
    icon: 'text',
  },
  [WELL_VOLUME_ATTRIBUTE_SENTINEL]: {
    attribute: WELL_VOLUME_ATTRIBUTE_SENTINEL,
    displayName: 'Well volume',
    icon: 'number',
  },
};

export enum PlateProcedureParameterType {
  DATA_PARAMETER = 'DATA_PARAMETER',
  MATERIAL_INPUT = 'MATERIAL_INPUT',
}

/**
 * Shared interface for plate data parameters and material inputs.
 */
export interface PlateProcedureParameter<
  TParameter extends PlateProcedureParameterType,
  TSchemaField extends MinimalSchemaField,
> {
  /** The type of the parameter */
  type: TParameter;
  /** The display name of the parameter */
  name: string;
  /** Whether this parameter is associated with the same step as the plate */
  inStep: boolean;
  /** Planned values for the parameter */
  plannedValues: Array<MinimalSchemaTag>;
  /** The API ID of the parameter version */
  versionId: string;
  /** The schema field definition for the value of the parameter */
  schemaField: TSchemaField;
  /** Optionally specify if the parameter is out of the current worksheet context */
  outOfContext?: boolean;
}

/**
 * A data parameter to be set by the plate. Each well is expected to have a value for each data parameter
 * in PlateMapWell.dataParameterTags.
 */
export type PlateDataParameter = PlateProcedureParameter<
  PlateProcedureParameterType.DATA_PARAMETER,
  MinimalSchemaField
>;

/**
 * A material input to be set by the plate.
 */
export type PlateMaterialInput = PlateProcedureParameter<
  PlateProcedureParameterType.MATERIAL_INPUT,
  SchemaField
>;

// Keep in line with benchling/inventory/fixed_plate/core/fixed_plate_constants.py
// Product limits for new plate map viz
export const PLATE_MAP_MAX_GRID_HEIGHT = 16;
export const PLATE_MAP_MAX_GRID_WIDTH = 24;

// Product limits for the new Fill Plate flow
export const PLATE_MAP_MAX_WELL_SOURCES = 10;
export const PLATE_MAP_MAX_DATA_PARAMETER_LAYERS = 10;
export const PLATE_MAP_MAX_WELL_SCHEMA_FIELD_LAYERS = 20;
const PLATE_MAP_MAX_LAYERS_PER_TYPE = 10;
export const PLATE_MAP_MAX_CONCENTRATION_LAYERS = PLATE_MAP_MAX_LAYERS_PER_TYPE;
export const PLATE_MAP_MAX_METADATA_LAYERS = PLATE_MAP_MAX_LAYERS_PER_TYPE;
export const MAX_METADATA_LAYER_NAME_LENGTH = 200;

export const SCHEMA_FIELD_NUMBER_WELL_COLOR = BLUE;
export const CUSTOM_LAYER_WELL_COLOR = GRAY_SEMIDARK;
export const WELL_VOLUME_WELL_COLOR = BLUE;
export const PLATE_MAP_COLOR_PALETTE = [
  '#7387CF',
  '#FC9F35',
  '#FF475A',
  '#0BCAB5',
  '#FF949F',
  '#5293FF',
  '#FBC2E4',
  '#F8D795',
  '#B5C5FF',
  '#57AFC2',
  '#ACDA11',
  '#F895D0',
  '#90DED6',
  '#E5BFEE',
  '#4BADD7',
  '#FF7280',
  '#57C887',
  '#0C9687',
  '#FFCF52',
  '#6BA1FF',
  '#FBC2C2',
  '#CD95F8',
  '#F9B93C',
  '#8DB800',
  '#C2EAFB',
];

export const ERROR_LEVEL_TO_COLOR: Record<PlateMapErrorLevel, string> = {
  [PlateMapErrorLevel.ERROR]: Color.RED_500,
  [PlateMapErrorLevel.WARNING]: Color.ORANGE_400,
  [PlateMapErrorLevel.INFO]: Color.BLUE_400,
};

export enum PlateMapPreferences {
  HAS_DISMISSED_TOO_BIG_PLATE_BANNER = 'HAS_DISMISSED_TOO_BIG_PLATE_BANNER',
}

export type PlateModalAnalyticsMetadata = AnalyticsMetadataForContext & {
  actionOrigin: ActionOrigin;
  plateId: string | null;
  modalType: FixedPlateModalType;
  eventGroupId: string;
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};

export interface PlateMapDisplayParams<
  K extends keyof AssignmentGroupValueTypeByLayer,
  V extends AssignmentGroupValueTypeByLayer[K],
> {
  displayLayer: K;
  plateMap: PlateMap;
  assignmentGroupsById: {[id: string]: AssignmentGroup<V>};
  attribute: PlateMapDisplayLayerAttribute;
  groupedValidationErrors: ValidationErrorsByLayerAndLevel;
}

export type GenericPlateMapDisplayParams = PlateMapDisplayParams<
  keyof AssignmentGroupValueTypeByLayer,
  AssignmentGroupValueTypeByLayer[keyof AssignmentGroupValueTypeByLayer]
>;

export interface MeasurementInput {
  value: number | null;
  unit: {
    unit_api_id: string;
    unit_version_number: number;
  } | null;
}

// TODO(BNCH-122786): Local flag to enable/disable the "Remove existing contents"
export const ENABLE_REMOVE_EXISTING_WELL_CONTENTS = false;
